const ADDRESS_ABBREVIATION_MAP: Record<string, string> = {
  st: 'Street',
  str: 'Street',
  street: 'Street',
  rd: 'Road',
  road: 'Road',
  dr: 'Drive',
  drive: 'Drive',
  ave: 'Avenue',
  av: 'Avenue',
  avenue: 'Avenue',
  ln: 'Lane',
  lane: 'Lane',
  blvd: 'Boulevard',
  boulevard: 'Boulevard',
  ct: 'Court',
  court: 'Court',
  pl: 'Place',
  place: 'Place',
  cir: 'Circle',
  circle: 'Circle',
  hwy: 'Highway',
  highway: 'Highway',
  pkwy: 'Parkway',
  parkway: 'Parkway',
  ter: 'Terrace',
  terrace: 'Terrace',
  trl: 'Trail',
  trail: 'Trail',
  sq: 'Square',
  square: 'Square',
  loop: 'Loop',
  ext: 'Extension',
  extension: 'Extension',
  n: 'North',
  s: 'South',
  e: 'East',
  w: 'West',
  north: 'North',
  south: 'South',
  east: 'East',
  west: 'West',
  ne: 'Northeast',
  nw: 'Northwest',
  se: 'Southeast',
  sw: 'Southwest',
  northeast: 'Northeast',
  northwest: 'Northwest',
  southeast: 'Southeast',
  southwest: 'Southwest',
  lp: 'Loop',
  wy: 'Way',
  way: 'Way',
  cv: 'Cove',
  cove: 'Cove',
};

/**
 * Returns { core, streetType } where:
 * - core = everything before the trailing street-type word (lowercased)
 * - streetType = the CANONICAL form of the suffix (e.g. "Road", "Street")
 *                or null if no known suffix found
 *
 * e.g. "3055 TOTEM Road" → { core: "3055 totem", streetType: "Road" }
 *      "3055 TOTEM RD"   → { core: "3055 totem", streetType: "Road" }
 *      "3055 TOTEM ST"   → { core: "3055 totem", streetType: "Street" }
 *      "3055 TOTEM"      → { core: "3055 totem", streetType: null }
 */
export const getAddressCoreAndType = (value: string): { core: string; streetType: string | null } => {
  if (!value) return { core: '', streetType: null };
  const words = value.toLowerCase().trim().split(/\s+/);

  if (words.length > 1) {
    const lastWord = words[words.length - 1];
    const canonical = ADDRESS_ABBREVIATION_MAP[lastWord];
    if (canonical) {
      return {
        core: words.slice(0, words.length - 1).join(' '),
        streetType: canonical,
      };
    }
  }

  return { core: words.join(' '), streetType: null };
};

/**
 * Strips the LAST word if it's a known street-type abbreviation/full-form,
 * and also strips the FIRST word if it's a known street-type abbreviation/full-form.
 * Returns the remaining "core" of the address lowercased and trimmed.
 *
 * e.g. "3055 TOTEM Road" → "3055 totem"
 *      "3055 TOTEM RD"   → "3055 totem"
 *      "Road 3055 TOTEM" → "3055 totem"
 *      "3055 TOTEM"      → "3055 totem"
 */
export const normalizeAddressCore = (value: string): string => {
  if (!value) return '';
  const words = value.toLowerCase().trim().split(/\s+/);
  let start = 0;
  let end = words.length - 1;

  if (end >= 0 && ADDRESS_ABBREVIATION_MAP[words[end]]) {
    end--;
  }

  if (start <= end && ADDRESS_ABBREVIATION_MAP[words[start]]) {
    start++;
  }

  return words.slice(start, end + 1).join(' ');
};

/**
 * Checks if the entered address contains a known street-type word anywhere.
 */
export const getAddressAbbreviationError = (value: string): string | null => {
  if (!value) return null;
  const words = value.toLowerCase().trim().split(/\s+/);
  const found = words.find((word) => ADDRESS_ABBREVIATION_MAP[word]);
  if (found) {
    return 'Address matches an existing address after abbreviation standardization.';
  }
  return null;
};

/**
 * Returns the core of an address (strips suffix only).
 * Kept for backward compatibility.
 */
export const getAddressCoreForComparison = (value: string): string => {
  return getAddressCoreAndType(value).core;
};
